// TwoYAxisTimePlot.jsx  (updated: CSV export button hidden)

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ArrowLeft, RefreshCw, /* Download, */ Calendar as CalendarIcon, Gauge } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import wqmsService from "../services/wqmsService";
import "../styles/twoY.css";

dayjs.extend(utc);

/* ----------------------- PARAM META ----------------------- */
//const defaultParamA = "ph_clean_avg";
//const defaultParamB = "dissolved_oxygen_mg_l_clean_avg";
  const defaultParamA = "ph_avg";
  const defaultParamB = "dissolved_oxygen_mg_l_avg";

{/*const PARAM_META = {
  ammonia_mg_l_avg: { label: "Ammonia_M", unit: "mg/L" },
  ammonia_mg_l_clean_avg: { label: "Ammonia_C", unit: "mg/L" },
  bod_mg_l_avg: { label: "BOD_M", unit: "mg/L" },
  bod_mg_l_clean_avg: { label: "BOD_C", unit: "mg/L" },
  cod_mg_l_avg: { label: "COD_M", unit: "mg/L" },
  cod_mg_l_clean_avg: { label: "COD_C", unit: "mg/L" },
  chloride_mg_l_avg: { label: "Chloride_M", unit: "mg/L" },
  chloride_mg_l_clean_avg: { label: "Chloride_C", unit: "mg/L" },
  toc_mg_l_avg: { label: "TOC_M", unit: "mg/L" },
  toc_mg_l_clean_avg: { label: "TOC_C", unit: "mg/L" },
  doc_mg_l_avg: { label: "DOC_M", unit: "mg/L" },
  doc_mg_l_clean_avg: { label: "DOC_C", unit: "mg/L" },
  tss_mg_l_avg: { label: "TSS_M", unit: "mg/L" },
  tss_mg_l_clean_avg: { label: "TSS_C", unit: "mg/L" },
  ortho_phosphates_mg_l_avg: { label: "Ortho-Phosphates_M", unit: "mg/L" },
  ortho_phosphates_mg_l_clean_avg: { label: "Ortho-Phosphates_C", unit: "mg/L" },
  cadmium_mg_l_avg: { label: "Cadmium_M", unit: "mg/L" },
  cadmium_mg_l_clean_avg: { label: "Cadmium_C", unit: "mg/L" },
  lead_mg_l_avg: { label: "Lead_M", unit: "mg/L" },
  lead_mg_l_clean_avg: { label: "Lead_C", unit: "mg/L" },
  nickel_ug_l_avg: { label: "Nickel_M", unit: "µg/L" },
  nickel_ug_l_clean_avg: { label: "Nickel_C", unit: "µg/L" },
  chromium_mg_l_avg: { label: "Chromium_M", unit: "mg/L" },
  chromium_mg_l_clean_avg: { label: "Chromium_C", unit: "mg/L" },
  ph_avg: { label: "pH_M", unit: "" },
  ph_clean_avg: { label: "pH_C", unit: "" },
  redox_mv_avg: { label: "ORP_M", unit: "mV" },
  redox_mv_clean_avg: { label: "ORP_C", unit: "mV" },
  temperature_c_avg: { label: "Temperature", unit: "°C" },
  turbidity_ntu_avg: { label: "Turbidity_M", unit: "NTU" },
  turbidity_ntu_clean_avg: { label: "Turbidity_C", unit: "NTU" },
  color_hazen_avg: { label: "Color", unit: "Hazen" },
  //level_m_avg: { label: "Water Level", unit: "m" },
  conductivity_us_cm_avg: { label: "Conductivity_M", unit: "µS/cm" },
  conductivity_us_cm_clean_avg: { label: "Conductivity_C", unit: "µS/cm" },

  dissolved_oxygen_mg_l_avg: { label: "Dissolved Oxygen_M", unit: "mg/L" },
  dissolved_oxygen_mg_l_clean_avg: { label: "Dissolved Oxygen_C", unit: "mg/L" },
};
*/}
const PARAM_META = {
  ammonia_mg_l_avg: { label: "Ammonia", unit: "mg/L" },
  bod_mg_l_avg: { label: "BOD", unit: "mg/L" },
  cod_mg_l_avg: { label: "COD", unit: "mg/L" },
  chloride_mg_l_avg: { label: "Chloride", unit: "mg/L" },
  toc_mg_l_avg: { label: "TOC", unit: "mg/L" },
  doc_mg_l_avg: { label: "DOC", unit: "mg/L" },
  tss_mg_l_avg: { label: "TSS", unit: "mg/L" },

  ph_avg: { label: "pH", unit: "" },
  redox_mv_avg: { label: "ORP", unit: "mV" },
  temperature_c_avg: { label: "Temperature", unit: "°C" },
  turbidity_ntu_avg: { label: "Turbidity", unit: "NTU" },
  color_hazen_avg: { label: "Color", unit: "Hazen" },
  conductivity_us_cm_avg: { label: "Conductivity", unit: "µS/cm" },

  dissolved_oxygen_mg_l_avg: { label: "Dissolved Oxygen", unit: "mg/L" },
};
const metaOf = (key) => {
  const m = PARAM_META[key] || { label: key, unit: "" };
  return { ...m, pretty: m.unit ? `${m.label} (${m.unit})` : m.label };
};

const allParams = Object.keys(PARAM_META).map((key) => {
  const m = metaOf(key);
  return { key, label: m.pretty };
});

/* ----------------------- misc helpers ---------------------- */
const fmt = (ts) => dayjs.utc(ts).format("YYYY-MM-DD HH:mm");
const pickColor = (key) => {
  const palette = ["#2563eb", "#16a34a", "#dc2626", "#7c3aed", "#ea580c", "#0891b2", "#b91c1c", "#15803d"];
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
};

function spanDays(startISO, endISO) {
  return Math.max(0, dayjs.utc(endISO).diff(dayjs.utc(startISO), "day", true));
}

function mapResForApiStrict(res) {
  if (res === "minute") return "minute";
  if (res === "hour") return "hour";
  return "day"; // day/week/month/year -> day
}

function formatTick(ts, res) {
  const d = dayjs.utc(ts);
  switch (res) {
    case "hour":
      return d.format("DD MMM HH:mm");
    case "day":
      return d.format("DD MMM");
    case "week":
      return `Week of ${d.format("DD MMM")}`;
    case "month":
      return d.format("MMM YYYY");
    case "year":
      return d.format("YYYY");
    default:
      return d.format("DD MMM");
  }
}

// Generic rollup to week/month/year averages (ignores nulls)
function rollup(rows, unit, kA, kB) {
  const buckets = new Map();

  for (const r of rows) {
    const bucketStart = dayjs.utc(r.ts).startOf(unit); // "week" | "month" | "year"
    const key = bucketStart.toISOString();

    const b = buckets.get(key) || { ts: bucketStart.toISOString(), a: [], b: [] };

    const va = r[kA];
    const vb = r[kB];

    if (va != null && Number.isFinite(va)) b.a.push(Number(va));
    if (vb != null && Number.isFinite(vb)) b.b.push(Number(vb));

    buckets.set(key, b);
  }

  const avg = (arr) => (arr.length ? arr.reduce((s, x) => s + x, 0) / arr.length : null);

  return [...buckets.values()]
    .map((b) => ({ ts: b.ts, [kA]: avg(b.a), [kB]: avg(b.b) }))
    .sort((a, b) => new Date(a.ts) - new Date(b.ts));
}

/* ======================= Component ======================= */
export default function TwoYAxisTimePlot() {
  const navigate = useNavigate();

  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState("");

  const [paramA, setParamA] = useState(defaultParamA);
  const [paramB, setParamB] = useState(defaultParamB);

  const today = dayjs.utc();
  const [start, setStart] = useState(today.subtract(7, "day").format("YYYY-MM-DD"));
  const [end, setEnd] = useState(today.format("YYYY-MM-DD"));

  // ✅ DEFAULT: Hourly selected on load
  const RES_OPTIONS = [
    { value: "hour", label: "Hourly" },
    { value: "day", label: "Daily" },
    { value: "week", label: "Weekly" },
    { value: "month", label: "Monthly" },
    { value: "year", label: "Yearly" },
  ];
  const [resMode, setResMode] = useState("hour"); // hour|day|week|month|year

  const [effectiveRes, setEffectiveRes] = useState("hour");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resNote, setResNote] = useState("");

  const metaA = useMemo(() => metaOf(paramA), [paramA]);
  const metaB = useMemo(() => metaOf(paramB), [paramB]);
  const colorA = pickColor(paramA);
  const colorB = pickColor(paramB);

  // load stations
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await wqmsService.getStations({ timeRange: "10", force: true });
        if (!mounted) return;
        setStations(Array.isArray(list) ? list : []);
        if (!stationId && list.length) setStationId(String(list[0].id));
      } catch (e) {
        console.error(e);
        setStations([]);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = useCallback(async () => {
    if (!stationId) return;
    setLoading(true);
    setError("");
    setResNote("");

    try {
      // 1) user-selected resolution
      const eff = resMode;
      setEffectiveRes(eff);

      // 2) ask API using strict res (week/month/year -> day)
      const apiRes = mapResForApiStrict(eff);

      const qs = new URLSearchParams({
        stationId,
        params: [paramA, paramB].join(","),
        start,
        end,
        res: apiRes, // hour|day
      }).toString();

      //const resp = await fetch(`/api/timeseries/compare?${qs}`);
    const token = localStorage.getItem("token");

    const resp = await fetch(`/api/timeseries/compare?${qs}`, {
      method: "GET",
      headers: {
       Authorization: `Bearer ${token}`,
      },
    });
      if (!resp.ok) throw new Error(`API ${resp.status}`);
      const data = await resp.json();

      const base = (Array.isArray(data) ? data : [])
        .map((r) => ({
          ts: r.ts,
          [paramA]: r[paramA] == null || Number.isNaN(r[paramA]) ? null : Number(r[paramA]),
          [paramB]: r[paramB] == null || Number.isNaN(r[paramB]) ? null : Number(r[paramB]),
        }))
        .sort((a, b) => new Date(a.ts) - new Date(b.ts));

      // 3) rollup if needed
      let finalRows = base;
      if (eff === "week" || eff === "month" || eff === "year") {
        finalRows = rollup(base, eff, paramA, paramB);
        setResNote(`Showing ${eff} averages (rolled up from daily).`);
      } else {
        setResNote("");
      }

      setRows(finalRows);
    } catch (e) {
      setError(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [stationId, paramA, paramB, start, end, resMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="twoy-wrap">
      <div className="admin-page-header">
        <div className="admin-header-row">
          <div className="admin-header-back">
            <button type="button" className="admin-btn admin-btn-secondary" onClick={() => navigate(-1)}>
              <ArrowLeft size={16} />
              Back
            </button>
          </div>
          <div className="admin-header-main">
            <h1 className="admin-page-title">
              <Gauge size={22} /> 2-Y Time Plot
            </h1>
            <p className="admin-page-subtitle">
              Compare any two parameters for a single station across a chosen time range.
            </p>
            <div className="admin-header-chips">
              <span className="twoy-chip">
                {dayjs.utc(start).format("YYYY-MM-DD")} → {dayjs.utc(end).format("YYYY-MM-DD")}
              </span>
              <span className="twoy-chip">
                {RES_OPTIONS.find((o) => o.value === resMode)?.label}
              </span>
            </div>
          </div>
          <div className="admin-header-actions">
            <button type="button" className="admin-btn admin-btn-primary" onClick={fetchData} title="Refresh">
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="twoy-controls">
        <div className="twoy-field">
          <label>Station</label>
          <select value={stationId} onChange={(e) => setStationId(e.target.value)}>
            {stations.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div className="twoy-field">
          <label>Parameter A (Left Y)</label>
          <select value={paramA} onChange={(e) => setParamA(e.target.value)}>
            {allParams.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div className="twoy-field">
          <label>Parameter B (Right Y)</label>
          <select value={paramB} onChange={(e) => setParamB(e.target.value)}>
            {allParams.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div className="twoy-field">
          <label>Aggregation</label>
          <select value={resMode} onChange={(e) => setResMode(e.target.value)}>
            {RES_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="twoy-field">
          <label>Start (PKT)</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>

        <div className="twoy-field">
          <label>End (PKT)</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
      </div>

      {/* Chart */}
      <div className="twoy-card">
        <div className="twoy-card-head">
          <CalendarIcon size={16} />
          <div className="twoy-chip">Station {stationId}</div>
          <div className="twoy-card-meta">
            A: {metaA.pretty} • B: {metaB.pretty}
          </div>
        </div>

        {resNote && <div className="text-xs text-amber-600 mb-2">{resNote}</div>}

        {error ? (
          <div className="text-red-600">{error}</div>
        ) : (
          <div style={{ width: "100%", height: 420 }}>
            <ResponsiveContainer>
              <LineChart data={Array.isArray(rows) ? rows : []} margin={{ top: 12, right: 24, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="ts" tickFormatter={(v) => formatTick(v, effectiveRes)} minTickGap={24} />
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  tickFormatter={(v) => `${v}`}
                  label={{ value: metaA.pretty, angle: -90, position: "insideLeft" }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tickFormatter={(v) => `${v}`}
                  label={{ value: metaB.pretty, angle: 90, position: "insideRight" }}
                />

                <Tooltip
                  labelFormatter={(l) => fmt(l)}
                  formatter={(val, name) => {
                    const pretty =
                      name === paramA ? `A: ${metaA.pretty}` : name === paramB ? `B: ${metaB.pretty}` : name;
                    const num = val == null || Number.isNaN(val) ? "" : Number(val).toFixed(2);
                    return [num, pretty];
                  }}
                />
                <Legend
                  formatter={(rawKey) =>
                    rawKey === paramA ? `A: ${metaA.pretty}` : rawKey === paramB ? `B: ${metaB.pretty}` : rawKey
                  }
                />

                <Line
                  type="monotone"
                  dataKey={paramA}
                  yAxisId="left"
                  name={metaA.pretty}
                  stroke={colorA}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey={paramB}
                  yAxisId="right"
                  name={metaB.pretty}
                  stroke={colorB}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {loading && <div className="text-sm text-slate-500 mt-2">Loading…</div>}
      </div>
    </div>
  );
}
